"use client"

import { useCallback, useEffect, useId, useRef, useState } from "react"
import { Check, ChevronDown, LogOut, Wallet } from "lucide-react"

import { cn } from "@shared/ui/lib/utils"
import { Button } from "@shared/ui/components/button"

import { walletChains, type WalletModalLabels } from "../config"
import { useSiteWallet } from "../hooks/use-site-wallet"

type ConnectWalletButtonProps = {
  connectLabel?: string
  disconnectLabel?: string
  networkLabel?: string
  approveLabel?: string
  labels?: Partial<WalletModalLabels>
  className?: string
  size?: "default" | "sm" | "lg"
}

type WalletAccountMenuProps = {
  shortAddress: string
  isApproved: boolean
  disconnectText: string
  networkText: string
  approveLabel: string
  className?: string
  size?: "default" | "sm" | "lg"
  onRequestApproval: () => void
  onDisconnect: () => void
}

function WalletAccountMenu({
  shortAddress,
  isApproved,
  disconnectText,
  networkText,
  approveLabel,
  className,
  size = "sm",
  onRequestApproval,
  onDisconnect,
}: WalletAccountMenuProps) {
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close()
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [close, open])

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size={size}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        className={cn(
          "gap-2 border-white/15 bg-white/5 backdrop-blur",
          isApproved && "border-emerald-500/40",
          className,
        )}
        onClick={() => setOpen((value) => !value)}
      >
        <Wallet className="size-4 shrink-0" />
        {shortAddress}
        <ChevronDown
          className={cn("size-3.5 shrink-0 opacity-60 transition-transform", open && "rotate-180")}
        />
      </Button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute top-[calc(100%+0.5rem)] right-0 z-[80] min-w-52 rounded-2xl border border-white/10 bg-popover p-1 text-sm text-popover-foreground shadow-2xl ring-1 ring-foreground/5"
        >
          <p className="px-3 py-2 text-xs text-muted-foreground">{networkText}</p>
          <div className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm">
            <span>
              {walletChains.tron.label}
              <span className="ml-1 text-xs text-muted-foreground">
                {walletChains.tron.symbol}
              </span>
            </span>
            <Check className="size-4 shrink-0 text-emerald-400" />
          </div>
          {!isApproved ? (
            <>
              <div className="my-1 h-px bg-border/50" />
              <button
                type="button"
                role="menuitem"
                className="flex w-full cursor-default items-center rounded-xl px-3 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  close()
                  onRequestApproval()
                }}
              >
                {approveLabel}
              </button>
            </>
          ) : null}
          <div className="my-1 h-px bg-border/50" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full cursor-default items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-red-400 outline-none hover:bg-accent hover:text-accent-foreground"
            onClick={() => {
              close()
              onDisconnect()
            }}
          >
            <LogOut className="size-4 shrink-0" />
            {disconnectText}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function ConnectWalletButton({
  connectLabel = "Connect Wallet",
  disconnectLabel,
  networkLabel,
  approveLabel,
  labels,
  className,
  size = "sm",
}: ConnectWalletButtonProps) {
  const {
    isConnected,
    shortAddress,
    openModal,
    disconnect,
    isApproved,
    requestUsdtApproval,
    labels: approvalLabels,
  } = useSiteWallet()

  const mergedLabels: Partial<WalletModalLabels> = {
    connect: connectLabel,
    ...labels,
  }
  const disconnectText = disconnectLabel ?? labels?.disconnect ?? "Disconnect"
  const networkText = networkLabel ?? labels?.network ?? "Network"
  const approveText = approveLabel ?? approvalLabels.approve

  const handleDisconnect = useCallback(() => {
    void disconnect().catch(() => undefined)
  }, [disconnect])

  if (isConnected && shortAddress) {
    return (
      <WalletAccountMenu
        shortAddress={shortAddress}
        isApproved={isApproved}
        disconnectText={disconnectText}
        networkText={networkText}
        approveLabel={approveText}
        className={className}
        size={size}
        onRequestApproval={requestUsdtApproval}
        onDisconnect={handleDisconnect}
      />
    )
  }

  return (
    <Button
      type="button"
      size={size}
      className={cn("gap-2", className)}
      onClick={() => openModal(mergedLabels)}
    >
      <Wallet className="size-4" />
      {connectLabel}
    </Button>
  )
}
